/* A board contains one of each selected part. Additional units are ordinary cart lines. */
(() => {
  'use strict';
  if (window.HalfpipedBoardCartQuantity) return;

  const physicalParts = new Set(['deck', 'grip', 'wheel', 'bearing', 'truck', 'hardware', 'skate_tool']);
  const isReservedProperty = key => /^_hp_board_/i.test(key) || key.trim().toLowerCase() === 'board customizer part';
  const isBoardLine = item => Object.keys(item?.properties || {}).some(key => /^_hp_board_/i.test(key));
  const standaloneProperties = properties => Object.fromEntries(Object.entries(properties || {}).filter(([key]) => !isReservedProperty(key)));
  const variantOf = item => String(item?.variant_id || item?.variantId || item?.id || '');
  const variantQuantity = (cart, variantId) => (cart?.items || []).reduce((sum, item) => (
    variantOf(item) === String(variantId) ? sum + (Number(item.quantity) || 0) : sum
  ), 0);
  const lineIdentity = item => window.HalfpipedBoardIntegrity.lineIdentity(item);
  const canAddExtra = item => isBoardLine(item)
    && physicalParts.has(item?.properties?._hp_board_bundle_component)
    && Number(item.original_price ?? item.price ?? Number(item.original_line_price) / Number(item.quantity)) > 0
    && !item.selling_plan_allocation && !item.selling_plan_id && !item.selling_plan;
  const repairMessage = 'Select Checkout to separate your saved extra items safely.';
  const displayText = value => typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';
  const optionText = value => /^default\s+title$/i.test(displayText(value)) ? '' : displayText(value);
  const displayItemLabel = item => {
    if (!item) return '';
    const title = optionText(item.product_title || item.product?.title || item.productTitle || item.name || item.title);
    if (!title) return '';
    const namedOptions = item.options_with_values || item.selectedOptions;
    let options = Array.isArray(namedOptions) ? namedOptions.flatMap(option => {
      const value = optionText(option?.value);
      const name = displayText(option?.name);
      return value ? [name && !/^title$/i.test(name) ? `${name}: ${value}` : value] : [];
    }) : [];
    if (!options.length) {
      const variant = optionText(item.variant_title || item.variantTitle);
      options = variant ? [variant] : Array.isArray(item.variantOptions) ? item.variantOptions.map(optionText).filter(Boolean) : [];
    }
    if (!options.length && item.selectedSize) options.push(`Width: ${displayText(item.selectedSize)}`);
    return `${title}${options.length ? ` — ${[...new Set(options)].join(' / ')}` : ''}`;
  };
  const itemFromCartRow = row => ({
    product_title: row?.querySelector?.('.cart-items__title')?.textContent?.trim() || '',
    options_with_values: [...(row?.querySelectorAll?.('.cart-items__variant') || [])].map(option => ({
      name: option.querySelector('dt')?.textContent?.trim().replace(/:$/, '') || '',
      value: option.querySelector('dd')?.textContent?.trim() || '',
    })),
  });
  const stockItemsFromAvailability = (availability, items = [], { includeUnavailable = false } = {}) => {
    const ids = new Set((availability?.removeBoards || []).filter(board => board.reason === 'INSUFFICIENT_STOCK'
      || (includeUnavailable && board.reason === 'VARIANT_UNAVAILABLE'))
      .flatMap(board => board.unavailableVariantIds || []).map(id => String(id).split('/').pop()));
    for (const issue of availability?.blockingIssues || []) {
      if (issue.code === 'CART_VARIANT_INSUFFICIENT_STOCK') {
        for (const id of issue.variantIds || (issue.variantId ? [issue.variantId] : [])) ids.add(String(id).split('/').pop());
      }
    }
    const named = new Map();
    for (const item of items) {
      const id = variantOf(item).split('/').pop();
      if (ids.has(id) && !named.has(id) && displayItemLabel(item)) named.set(id, item);
    }
    return [...named.values()];
  };
  const stockItemList = items => [...new Set((items || []).map(displayItemLabel).filter(Boolean))].map(label => `• ${label}`).join('\n');
  const quantityLimitError = (maxQuantity, { stockLimited = false, cartQuantity, item } = {}) => stockLimited
    ? stockError({ exhausted: Number.isFinite(cartQuantity) && cartQuantity >= maxQuantity, items: item ? [item] : [] })
    : Object.assign(new Error(
      `Hey there! So sorry, that quantity isn’t available for this option.${item && displayItemLabel(item) ? `\n\n• ${displayItemLabel(item)}` : ''}`
    ), { code: 'BOARD_EXTRA_QUANTITY_LIMIT' });
  const stockError = ({ exhausted = false, items = [] } = {}) => {
    const list = stockItemList(items);
    return Object.assign(new Error(list
      ? `Hey there! So sorry, ${exhausted ? 'there’s no more extra stock available for' : 'there isn’t enough stock for that quantity of'}:\n\n${list}`
      : exhausted ? 'Hey there! So sorry, there’s no more extra stock of this option available.'
        : 'Hey there! So sorry, there isn’t enough stock for that quantity.'
    ), { code: 'BOARD_EXTRA_STOCK_LIMIT', stockItems: items });
  };
  const notifyQuantityLimit = error => {
    if (!['BOARD_EXTRA_QUANTITY_LIMIT', 'BOARD_EXTRA_STOCK_LIMIT'].includes(error?.code)) return false;
    window.alert(error.message);
    return true;
  };
  let activeActions = 0;
  const hasBoard = cart => (cart?.items || []).some(isBoardLine);

  const assertReady = cart => {
    if (window.HalfpipedBoardQuantityRepair?.hasPending?.(cart)) throw new Error(repairMessage);
    if (!window.HalfpipedBoardIntegrity?.requestCart || !window.HalfpipedBoardIntegrity?.withCartLock
      || !window.HalfpipedCartMutations?.begin || !window.HalfpipedBoardCheckout?.requestServer) {
      throw new Error('Please reload the page before adding extra board parts.');
    }
  };
  const revealExtra = ({ result, cart, drawer, body } = {}) => {
    const target = result?.extraTarget;
    const mutations = window.HalfpipedCartMutations;
    if (!target || !result.stamp?.confirmed || result.stamp.cartRevision !== mutations?.getRevision()
      || mutations.isPending?.() || mutations.getPendingCount?.() > 0
      || cart?.token !== result.cart?.token || !drawer?.isConnected || !body?.isConnected
      || !drawer.classList.contains('is-open') || drawer.classList.contains('is-closing') || !drawer.contains(body)) return false;
    const matches = (cart.items || []).filter(item => !isBoardLine(item) && lineIdentity(item) === target.identity);
    if (matches.length !== 1 || matches[0].quantity !== target.quantity) return false;
    const item = matches[0];
    const rows = [...body.querySelectorAll('[data-cart-item]')].filter(row => row.dataset.lineKey === String(item.key)
      && row.dataset.pending !== 'true' && Number(row.dataset.quantity) === target.quantity);
    if (rows.length !== 1) return false;
    const row = rows[0];
    const expectedIdentity = window.HalfpipedCartDrawerDom?.identity(item);
    if (expectedIdentity && row.dataset.cartLineIdentity !== expectedIdentity) return false;
    const viewport = body.getBoundingClientRect();
    const bounds = row.getBoundingClientRect();
    const viewportTop = viewport.top + body.clientTop;
    if (body.clientHeight <= 0 || bounds.height <= 0 || bounds.width <= 0) return false;
    if (bounds.top >= viewportTop + 8 && bounds.bottom <= viewportTop + body.clientHeight - 8) return false;
    const inset = Math.max(8, (body.clientHeight - bounds.height) / 2);
    const top = Math.min(Math.max(0, body.scrollTop + bounds.top - viewportTop - inset), Math.max(0, body.scrollHeight - body.clientHeight));
    if (Math.abs(top - body.scrollTop) < 1) return false;
    body.scrollTo({ top, left: body.scrollLeft,
      behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    return true;
  };
  const change = async ({ key, quantity, cart, expectedQuantity, maxQuantity, maxStockLimited = false, onCart, releaseDebounce } = {}) => {
    if (cart && !hasBoard(cart) && !window.HalfpipedBoardQuantityRepair?.hasPending?.(cart)) return { handled: false, cart };
    assertReady(cart);
    const helper = window.HalfpipedBoardIntegrity;
    const mutations = window.HalfpipedCartMutations;
    const original = cart?.items?.find(line => line.key === key);
    const finish = mutations.begin();
    activeActions += 1;
    releaseDebounce?.();
    let attemptedWrite = false;
    let reconciledWrite = false;
    try {
      return await helper.withCartLock(async lockToken => {
        const revision = mutations.getRevision();
        const assertUnchanged = () => {
          if (mutations.getRevision() !== revision || mutations.getPendingCount() > activeActions) {
            throw new Error('Your cart changed. Please review it and try again.');
          }
        };
        assertUnchanged();
        const before = await helper.requestCart();
        assertReady(before);
        assertUnchanged();
        if (cart?.token && cart.token !== before.token) throw new Error('Your cart changed. Please reopen it and try again.');
        const identityMatches = original ? before.items.filter(item => lineIdentity(item) === lineIdentity(original)) : [];
        const item = before.items.find(line => line.key === key) || (identityMatches.length === 1 ? identityMatches[0] : null);
        if (!item) throw new Error('This cart item changed. Please reopen your cart.');
        if (!Number.isSafeInteger(quantity) || quantity < 0) throw new Error('Please choose a valid quantity.');
        const marked = isBoardLine(item);
        const previousQuantity = expectedQuantity ?? original?.quantity;
        if (!marked && previousQuantity !== undefined && Number(previousQuantity) !== item.quantity) {
          throw new Error('This item’s quantity changed in another cart view. Please reopen your cart before updating it.');
        }
        if (marked && Number(item.quantity) !== 1) throw new Error(repairMessage);
        if (marked && !canAddExtra(item)) throw new Error('This choice is included once with your board.');
        if (marked && quantity <= 1) return { handled: true, cart: before };
        const extra = marked ? quantity - 1 : quantity - item.quantity;
        const variantId = variantOf(item);
        if (extra > 0 && Number.isFinite(maxQuantity) && variantQuantity(before, variantId) + extra > maxQuantity) {
          throw quantityLimitError(maxQuantity, { stockLimited: maxStockLimited, cartQuantity: variantQuantity(before, variantId), item });
        }
        const ordinary = marked ? { variant_id: variantId, properties: standaloneProperties(item.properties) } : item;
        const targets = marked ? before.items.filter(line => !isBoardLine(line) && lineIdentity(line) === lineIdentity(ordinary)) : [item];
        if (targets.length > 1) throw new Error('These items have separate prices. Please add the extra item from its product page.');
        const target = targets[0];
        const targetQuantity = marked ? (Number(target?.quantity) || 0) + extra : quantity;
        const candidateItems = before.items.map(line => line === target ? { ...line, quantity: targetQuantity } : line).filter(line => line.quantity > 0);
        if (!target) candidateItems.push({ ...ordinary, quantity: targetQuantity });
        const candidate = { ...before, integrityPreview: true, items: candidateItems };
        if (extra > 0 && hasBoard(before)) {
          const availability = await window.HalfpipedBoardCheckout.requestServer('availability', candidate);
          assertUnchanged();
          if (availability.removeBoards.length || availability.blockingIssues.length) {
            const onlyStockIssues = availability.removeBoards.every(board => board.reason === 'INSUFFICIENT_STOCK')
              && availability.blockingIssues.every(issue => issue.code === 'CART_VARIANT_INSUFFICIENT_STOCK');
            if (onlyStockIssues) {
              // A rejected single extra exhausts this option only when the
              // server identifies this exact variant as the stock shortage.
              const exhausted = extra === 1 && availability.blockingIssues.length === 0
                && availability.removeBoards.every(board => board.unavailableVariantIds?.length > 0
                  && board.unavailableVariantIds.every(id => String(id).split('/').pop() === variantId));
              throw stockError({ exhausted, items: stockItemsFromAvailability(availability, before.items) });
            }
            throw new Error('Your cart needs review before this quantity can be added. Your cart has not changed.');
          }
        }
        assertUnchanged();
        const path = target ? 'cart/change.js' : 'cart/add.js';
        const payload = target ? { id: target.key, quantity: targetQuantity }
          : { items: [{ id: variantId, quantity: targetQuantity, properties: ordinary.properties }] };
        let writeError = null;
        try {
          attemptedWrite = true;
          const response = await window.fetch(`${window.Shopify?.routes?.root || '/'}${path}`, {
            method: 'POST', credentials: 'same-origin', hpCartLockToken: lockToken,
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
          });
          const result = await response.json();
          if (!response.ok || result.status || result.errors) throw new Error(result.description || result.message || 'Could not update your extra items.');
        } catch (error) {
          // A 422 can add the available portion. Reconcile every response;
          // never automatically replay an additive write after uncertainty.
          writeError = error;
        }
        const cartRevision = mutations.getRevision();
        const verifiedCart = await helper.requestCart();
        const stamp = { confirmed: cartRevision === mutations.getRevision() && mutations.getPendingCount() <= activeActions, cartRevision };
        await onCart?.(verifiedCart, stamp);
        if (stamp.confirmed) {
          reconciledWrite = true;
          helper.prepare(verifiedCart);
          mutations.acknowledgeCart(stamp.cartRevision);
        }
        if (writeError) {
          const label = displayItemLabel(item);
          throw new Error(`${writeError.message || 'The update could not be confirmed.'} Your cart shows the items actually added.${label ? `\n\n• ${label}` : ''}`);
        }
        const destinations = marked && extra > 0 && stamp.confirmed
          ? verifiedCart.items.filter(line => !isBoardLine(line) && lineIdentity(line) === lineIdentity(ordinary)) : [];
        const extraTarget = destinations.length === 1 && destinations[0].quantity === targetQuantity
          ? { key: String(destinations[0].key), identity: lineIdentity(destinations[0]), quantity: targetQuantity } : null;
        return { handled: true, cart: verifiedCart, stamp, extraTarget };
      });
    } catch (error) {
      // Rejected preflight did not mutate Shopify and must not poison a later
      // checkout of the unchanged, valid board.
      finish(attemptedWrite && !reconciledWrite ? error : undefined);
      throw error;
    } finally {
      activeActions -= 1;
      finish();
    }
  };
  window.HalfpipedBoardCartQuantity = {
    isBoardLine, hasBoard, canAddExtra, standaloneProperties, lineIdentity, variantQuantity, change, assertReady, revealExtra,
    quantityLimitError, stockError, notifyQuantityLimit, displayItemLabel, itemFromCartRow, stockItemsFromAvailability,
    isPending: () => activeActions > 0,
  };
})();
